import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { shiftTimingScreenServices } from 'services/ReferenceScreenServices/shiftTimingsServices';
import {
  ShiftTiming,
  ShiftTimingResponse,
  ShiftTimingSingleResponse
} from 'Interfaces/ReferenceInterfaces/ShiftTimingsInterfaces';

export class ShiftTimingStore {
  shiftTimings: ShiftTiming[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedShiftTiming: ShiftTiming | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchShiftTimings = async (page = 1, pageSize = 100) => {
    this.isLoading = true;
    try {
      const response = await shiftTimingScreenServices.getAllShiftTimings(page, pageSize);
      runInAction(() => {
        this.shiftTimings = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching shift timings');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollShiftTimings = async (page = 1, pageSize = 100) => {
    this.isPolling = true;
    try {
      const response = await shiftTimingScreenServices.getAllShiftTimings(page, pageSize);
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.shiftTimings) !== JSON.stringify(response.data)) {
          this.shiftTimings = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if(this.environment === 'development') {
        console.error('Error polling shift timings:', error);
      }
      toast.error('Error polling shift timings!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 100) => {
    await this.fetchShiftTimings(page, pageSize);
  };

  createShiftTiming = async (shiftTimingData: Partial<ShiftTiming>): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await shiftTimingScreenServices.createShiftTiming(shiftTimingData);
      if (response.status) {
        toast.success('Shift timing created successfully');
        await this.fetchShiftTimings(); // Refresh the list
        return true;
      } else {
        toast.error(response.message || 'Failed to create shift timing');
        return false;
      }
    } catch (error: any) {
      // Handle server error responses
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Error creating shift timing');
      }
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateShiftTiming = async (
    id: number,
    shiftTimingData: Partial<ShiftTiming>
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await shiftTimingScreenServices.updateShiftTiming(id, shiftTimingData);
      if (response.status) {
        toast.success('Shift timing updated successfully');
        await this.fetchShiftTimings(); // Refresh the list
        return true;
      } else {
        toast.error(response.message || 'Failed to update shift timing');
        return false;
      }
    } catch (error: any) {
      // Handle server error responses
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Error updating shift timing');
      }
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteShiftTiming = async (id: number): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await shiftTimingScreenServices.deleteShiftTiming(id);
      if (response.status) {
        toast.success('Shift timing deleted successfully');
        await this.fetchShiftTimings(); // Refresh the list
        return true;
      } else {
        toast.error(response.message || 'Failed to delete shift timing');
        return false;
      }
    } catch (error: any) {
      // Handle server error responses
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Error deleting shift timing');
      }
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedShiftTiming = (shiftTiming: ShiftTiming | null) => {
    this.selectedShiftTiming = shiftTiming;
  };
}

export const shiftTimingStore = new ShiftTimingStore();
